/** @jsxImportSource theme-ui */
import { Box, Button, Container, Spinner, Text } from "theme-ui";
import Logo from "../components/Logo";
import UploadArea from "../components/UploadArea";
import { useRef, useState } from "react";
import ResultArea from "../components/ResultArea";
import { ImageRequest } from "../types/imagerequest";
import apiInvocation from "../api/apiInvocation";
import PreviousRequests from "../components/PreviousRequests";
import Icon from "../components/common/Icon";
import Tooltip from "../components/common/Tooltip";
import { useMsal } from "@azure/msal-react";
import { useNavigate } from "react-router-dom";

const Home = () => {
    const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
    const [result, setResult] = useState<ImageRequest | undefined>(undefined);
    const [isPreviousRequestsShown, setIsPreviousRequestsShown] = useState(false);
    const [isFetchingPreviousRequests, setIsFetchingPreviousRequests] =
        useState(false);
    const [previousRequests, setPreviousRequests] = useState<ImageRequest[]>([]);
    const resultScrollRef = useRef<HTMLDivElement>(null);
    const previousRequestsScrollRef = useRef<HTMLDivElement>(null);
    const { instance } = useMsal();
    const activeAccount = instance.getActiveAccount();
    const isAdmin = activeAccount?.idTokenClaims?.roles?.includes("Admin");
    const navigate = useNavigate();

    // Handler to clear the current result when deleted
    const handleCurrentResultDelete = () => {
        setResult(undefined);
    };

    // Handler to remove deleted item from previous requests
    const handlePreviousRequestDelete = (deletedId: string) => {
        setPreviousRequests((prev) => prev.filter((request) => request.id !== deletedId));
    };

    const fetchPreviousRequests = async () => {
        setIsFetchingPreviousRequests(true);
        const response = await apiInvocation.fetchPreviousRequests();
        setPreviousRequests(response);
        setIsFetchingPreviousRequests(false);
        setIsPreviousRequestsShown(true);
        setTimeout(() => {
            previousRequestsScrollRef.current?.scrollIntoView({ behavior: "smooth" });
        }, 200);
    };

    return (
        <Container
            sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minHeight: "100vh",
                overflowY: "auto",
            }}
        >
            <Logo />

            <Text sx={{ fontSize: [1, 5], color: "secondaryText" }}>
                Upload a picture of any plant or animal, and our AI will identify it for
                you.
            </Text>

            <UploadArea
                responseGenerationStarted={() => {
                    setIsGeneratingResponse(true);
                    setTimeout(() => {
                        resultScrollRef.current?.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                }}
                responseGenerated={(result: ImageRequest) => {
                    setResult(result);
                    setIsGeneratingResponse(false);
                    setTimeout(() => {
                        resultScrollRef.current?.scrollIntoView({ behavior: "smooth" });
                    }, 200);
                }}
            />

            <Box
                sx={{
                    color: "tertiaryText",
                    fontSize: [0, 2],
                    mt: 4,
                    textAlign: "center",
                }}
            >
                <Text sx={{ fontWeight: "regular" }}>
                    You must select at least one option for location (coordinates or site selection)
                </Text>
            </Box>

            <Box ref={resultScrollRef} />

            {(isGeneratingResponse || result) && (
                <ResultArea
                    isGeneratingResponse={isGeneratingResponse}
                    result={result}
                    onDeleteSuccess={handleCurrentResultDelete}
                />
            )}

            {!isPreviousRequestsShown && (
                <>
                    {isFetchingPreviousRequests ? (
                        <Spinner
                            size={24}
                            sx={{ color: "primaryText", mt: [9, 10], height: "44px" }}
                        />
                    ) : (
                        <Button
                            variant="secondary"
                            sx={{ mt: [9, 10], px: ["auto", 13] }}
                            onClick={fetchPreviousRequests}
                        >
                            Show Previous Submissions
                        </Button>
                    )}
                </>
            )}

            <Box ref={previousRequestsScrollRef} sx={{ pb: 10 }} />

            {isPreviousRequestsShown && (
                <Box sx={{ pb: 12 }}>
                    <PreviousRequests
                        previousRequests={previousRequests}
                        onDeleteSuccess={handlePreviousRequestDelete}
                    />
                </Box>
            )}

            <Box sx={{ mt: 13 }} />
            {isAdmin && (
                <Box
                    sx={{
                        position: "fixed",
                        bottom: [20, 60],
                        right: [20, 60],
                        display: "flex",
                        flexDirection: "column",
                        gap: 3,
                    }}
                >
                    <Box>
                        <Tooltip content="Users page">
                            <Button
                                variant="primary"
                                sx={{
                                    width: 60,
                                    height: 60,
                                    border: "2px solid #F5F9FC", // Adds border
                                }}
                            >
                                <Icon
                                    name="users"
                                    color="buttonText"
                                    size={24}
                                    onClick={() => navigate("/users")}
                                />
                            </Button>
                        </Tooltip>
                    </Box>
                    <Box>
                        <Tooltip content="Admin page">
                            <Button
                                variant="primary"
                                sx={{
                                    width: 60,
                                    height: 60,
                                    border: "2px solid #F5F9FC", // Adds border
                                }}
                            >
                                <Icon
                                    name="shield"
                                    color="buttonText"
                                    size={24}
                                    onClick={() => navigate("/admin")}
                                />
                            </Button>
                        </Tooltip>
                    </Box>
                </Box>
            )}
        </Container>
    );
};

export default Home;